import { CustomEventWithDetail, html, on, View } from 'rune-ts';
import klass from './LinkToInput.module.scss';
import { cx, cxIf, isUrl } from '../util';
import { Icon } from '../Icon/Icon.ts';
import { ButtonCTA, CTAButtonEvent } from '@modules/NewMaker/UI/ButtonCTA/ButtonsCTA.ts';
import { BlurEvent, EnterEvent, HandleInputEvent, Input } from '@modules/NewMaker/UI/Input/Input.ts';

export interface LinkToInputData {
  original_url: string;
  input_value: string;
  is_mobile?: boolean;
}

export class ConnectUrlEvent extends CustomEventWithDetail<{ original_url: string }> {}
export class InputBlurredEvent extends CustomEventWithDetail<{ input_value: string }> {}
export class EditModeEvent extends CustomEventWithDetail<{ input_value?: string }> {}

export class LinkToInput extends View<LinkToInputData> {
  private _originalUrl?: string;
  private _isInValid? = false;
  private _inputValue?: string;

  private inputView?: Input;
  private buttonCta?: ButtonCTA;

  private _fixedBtnWidthPx: number;

  constructor(data: LinkToInputData) {
    super(data);
    this._originalUrl = data.original_url;
    this._inputValue = data.input_value || this._originalUrl;
    this._fixedBtnWidthPx = this.data.is_mobile ? 112 : 136;
  }

  private _applyFixedButtonWidth() {
    if (!this.buttonCta) return;
    this.buttonCta.data.style = `width:${this._fixedBtnWidthPx}px`;
    this.buttonCta.redraw();
  }

  private _reset() {
    this._originalUrl = undefined;
    this._inputValue = undefined;
    this._isInValid = undefined;
  }

  get original_url(): string | undefined {
    return this._originalUrl;
  }

  set original_url(url: string | undefined) {
    this._originalUrl = url;
  }

  override template() {
    this.inputView = new Input({
      id: '1',
      name: 'nfc_link',
      type: 'text',
      placeholder: TT('nfc_product::placeholder'),
      value: this._inputValue ?? this._originalUrl,
      disabled: !!this._originalUrl,
    });

    this.buttonCta = this.buttonCta = new ButtonCTA({
      theme: this._originalUrl ? 'ghost' : 'filled',
      size: 'm',
      text: this._originalUrl ? TT('nfc_product::edit') : TT('nfc_product::connect'),
      disabled: false,
      style: `width:${this._fixedBtnWidthPx}px`,
    });

    return html`<div class="${klass.container}">
      <div class="${klass.linkToInput}">
        <div class="${cx(klass.input, cxIf(!!this._originalUrl, klass.disabled))}">
          ${this.inputView} ${this._originalUrl ? new Icon({ name: 'outlink' }) : ''}
        </div>
        <div class="${cx(klass.button, klass['fade-visible'])}">${this.buttonCta}</div>
      </div>
      <div class="${klass.infoWrapper}">
        ${this._originalUrl
          ? html.preventEscape(
              `<span class="${cx(klass.desc, klass.connected_msg)}">${TT('nfc_product::connected_msg')}</span>`,
            )
          : html.preventEscape(`<span class="${klass.desc}">${TT('nfc_product::description')}</span>`)}

        <span class="${klass.warning}" style="display: none;">${TT('nfc_product::warning')}</span>
      </div>
    </div>`;
  }

  override onRender() {
    this.addEventListener(HandleInputEvent, (e: CustomEvent) => {
      let value = e.detail.value.trim();
      if (!/^https?:\/\//.test(value)) {
        value = 'https://' + value;
      }

      this._inputValue = value;
      if (!this._inputValue || !this.inputView || !this.buttonCta) return;

      this.inputView.data.value = this._inputValue;

      const isValid = isUrl(this._inputValue);

      if (this._isInValid) {
        this._toggleWarning(isValid);
      }
    });

    this.addEventListener(CTAButtonEvent, async (e: CustomEvent) => {
      if (e.detail.text === TT('nfc_product::connect')) {
        await this._createByTagUrl();
      } else if (e.detail.text === TT('nfc_product::edit')) {
        const val = this._inputValue || this._originalUrl;

        this.dispatchEvent(EditModeEvent, {
          bubbles: true,
          detail: { input_value: val },
        });
        this._reset();
        this._inputValue = val;
        this.redraw();
      }
    });

    this.addEventListener(EnterEvent, async () => {
      if (this.buttonCta && !this.buttonCta.data.disabled) {
        requestAnimationFrame(() => this._createByTagUrl());
      }
    });

    this.addEventListener(BlurEvent, (e: CustomEvent) => {
      this._inputValue = e.detail.value;
      if (!this._inputValue) return;

      this._toggleWarning(isUrl(this._inputValue));
      this.dispatchEvent(InputBlurredEvent, {
        bubbles: true,
        detail: {
          input_value: this._inputValue,
        },
      });
    });
  }

  @on('click', 'svg')
  _clickIcon(_e: Event) {
    if (!this._originalUrl) return;

    window.open(this._originalUrl, '_blank');
  }

  private _toggleWarning(is_valid: boolean) {
    const $desc = this.element().querySelector(`.${klass.desc}`) as HTMLElement;
    const $warning = this.element().querySelector(`.${klass.warning}`) as HTMLElement;
    const $input = this.element().querySelector(`.${klass.input}`) as HTMLElement;

    if (is_valid) {
      $warning.style.display = 'none';
      $desc.style.display = '';
      $input.style.borderColor = '#d8d8d8';
    } else {
      $warning.style.display = '';
      $desc.style.display = 'none';
      $input.style.borderColor = '#d92a0f';
      this._isInValid = true;
    }
  }

  private _toggleStatusGuide() {
    const $desc = this.element().querySelector(`.${klass.desc}`) as HTMLElement;
    $desc.textContent = TT('nfc_product::connected_msg');
    $desc.style.color = '#009444';
  }

  private async _createByTagUrl() {
    if (!this._inputValue || !isUrl(this._inputValue)) {
      return this._toggleWarning(false);
    }

    this._originalUrl = this._inputValue;

    const $btn = document.querySelector(`.${klass.button}`) as HTMLElement;

    // 페이드 아웃
    $btn.classList.remove(klass['fade-visible']);
    $btn.classList.add(klass['fade-hidden']);
    await new Promise((r) => setTimeout(r, 200)); // 0.2s

    // 연결 완료 상태 세팅
    this.buttonCta?.setTheme('greenery');
    this.buttonCta?.setText(html.preventEscape(`${TT('nfc_product::connected')}`));
    this.buttonCta?.redraw();
    this._applyFixedButtonWidth();

    this._toggleStatusGuide();

    // 페이드 인 시작
    $btn.classList.remove(klass['fade-hidden']);
    $btn.classList.add(klass['fade-visible']);
    await new Promise((r) => setTimeout(r, 1200)); // 0.2s

    // 페이드 아웃
    $btn.classList.remove(klass['fade-visible']);
    $btn.classList.add(klass['fade-hidden']);
    await new Promise((r) => setTimeout(r, 200)); // 0.2s

    // 수정하기 상태 전환
    this.buttonCta?.setTheme('ghost');
    this.buttonCta?.setText(TT('nfc_product::edit'));
    this.buttonCta?.redraw();
    this._applyFixedButtonWidth();

    await new Promise((r) =>
      requestAnimationFrame(() => {
        $btn.classList.remove('fade-hidden');
        $btn.classList.add(klass['fade-visible']);
        r(null);
      }),
    );

    this.dispatchEvent(ConnectUrlEvent, {
      bubbles: true,
      detail: {
        original_url: this._originalUrl,
      },
    });

    this.redraw();
  }

  switchToMarpplizerMode(is_short: boolean) {
    if (!this.buttonCta) return;
    if (is_short) {
      this._fixedBtnWidthPx = 96;
      this._applyFixedButtonWidth();
    } else {
      this._fixedBtnWidthPx = this.data.is_mobile ? 112 : 136;
      this._applyFixedButtonWidth();
    }
  }
}
